<template>
  <div class="modern-password-reset-wrapper">
    <div v-if="!loading" class="password-reset-page">
      <div class="container">
        <div class="row password-reset-wrapper">
          <div class="col-12 ml-auto mr-auto align-self-center">
            <div class="password-reset-card">
              <!-- Logo Header -->
              <div class="password-reset-header">
                <img
                  src="../../theme-assets/images/logo/culogo-h.png"
                  class="logo-img"
                  alt="University Logo"
                />
              </div>

              <!-- Step 1: Enter User ID -->
              <div v-if="step === 1" class="password-reset-form">
                <h5 class="my-3 text-center">Request Password Reset</h5>
                <b-form @submit.prevent="getMaskedMobileNumbers">
                  <div class="form-group-modern">
                    <label class="input-label">Username</label>
                    <div class="input-wrapper">
                      <i class="fa fa-user input-icon"></i>
                      <b-form-input
                        v-model="username"
                        type="text"
                        class="modern-input"
                        required
                        placeholder="Enter your User ID" />
                    </div>
                  </div>

                  <b-button
                    v-if="!isButtonLoading"
                    type="submit"
                    class="password-reset-button mt-4"
                    block
                  >
                    <i class="fa fa-arrow-right"></i> Next
                  </b-button>
                  <b-button
                    v-else
                    class="password-reset-button loading mt-4"
                    block
                    disabled
                  >
                    <i class="fa fa-spinner fa-spin"></i> Loading...
                  </b-button>
                </b-form>
              </div>

              <!-- Step 2: Select Contact -->
              <div v-else-if="step === 2" class="password-reset-form">
                <h5 class="my-3 text-center">
                  Select a contact to reset password
                </h5>
                <b-form @submit.prevent="sendRecoveryLink">
                  <b-form-group >
                    <b-form-radio-group v-model="selectedMobileNumber" required class="">
                      <b-form-radio
                        v-for="(channel, idx) in maskedMobileNumbers"
                        :key="idx"
                        :value="channel.type"
                        class="d-block text-center mb-2 h5">
                        {{ channel.number }}
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                  <!-- 👇 Math verification section -->
                  <b-form-group class="text-left mt-3">
                    <label class="font-weight-500">
                      Please solve:
                      <strong>{{ num1 }}</strong> + <strong>{{ num2 }}</strong> = ?
                    </label>
                    <b-form-input
                      v-model.number="captchaAnswer"
                      type="number"
                      placeholder="Enter the result"
                      class="modern-input"
                      required
                    >
                  </b-form-input>
                  <div class="text-right" >
                    <small @click="generateCaptcha()" style="cursor:pointer;">Reload </small>
                  </div>
                  </b-form-group>

                  <b-button
                    v-if="!isButtonLoading"
                    type="submit"
                    class="password-reset-button mt-2"
                    block
                    :disabled="!selectedMobileNumber">
                    <i class="fa fa-paper-plane"></i> Send Password
                  </b-button>
                  <b-button
                    v-else
                    class="password-reset-button loading mt-4"
                    block
                    disabled
                  >
                    <i class="fa fa-spinner fa-spin"></i> Sending...
                  </b-button>
                </b-form>

                <div class="text-center">
                  <b-button
                    type="button"
                    variant="outline-secondary"
                    class="password-reset-back-button mt-3 w-50"
                    @click="
                      step = 1
                      username = ''
                      maskedMobileNumbers = []
                      selectedMobileNumber = null
                      isButtonLoading = false
                    "
                    block
                    :disabled="isButtonLoading"
                  >
                    <i class="fa fa-arrow-left"></i> Go Back
                  </b-button>
                </div>
              </div>

              <!-- Step 3: Success -->
              <div
                v-else-if="step === 3"
                class="password-reset-form text-center"
              >
                <h5 class="my-3">Check your messages</h5>
                <p class="password-reset-message mt-3">
                  A new password has been sent to your selected mobile number.
                  Please log in with your new password.
                </p>
                <b-button
                  class="password-reset-button mt-4"
                  @click="goToLoginHandler"
                  block
                >
                  <i class="fa fa-sign-in"></i> Go to Login
                </b-button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-else>
      <loader class="password-reset-loader"></loader>
    </div>
  </div>
</template>

<script>
import iziToast from 'izitoast'
import 'izitoast/dist/css/iziToast.min.css'
import {
  getRecoveryMobiles,
  sendNewPassword
} from '../../utils/PassowordRecovery'
import Loader from '../Helper/Loader'

export default {
  name: 'PasswordRecovery',
  components: { Loader },
  emits: ['goForLogin'],
  data() {
    return {
      step: 1,
      username: '',
      maskedMobileNumbers: [],
      selectedMobileNumber: null,
      loading: true,
      isButtonLoading: false,
      num1: 0,
      num2: 0,
      captchaAnswer: null
    }
  },
  methods: {
    async getMaskedMobileNumbers() {
      try {
        this.isButtonLoading = true
        const response = await getRecoveryMobiles(this.username)
        
        if (response.status) {
          // Transform the API response to match our component's structure
          this.maskedMobileNumbers = response.data.map(channel => ({
            type: channel.id,
            number: channel.number
          }))
          this.isButtonLoading = false
          this.step = 2
        } 
        else {
          iziToast.error({
            title: 'Error',
            message: response.details || 'Unable to find the user.'
          })
        }
      } catch (e) {
        // Extract meaningful backend error if available
        const errorMessage = 'An Unknown error occurred. Please try again.'
        iziToast.error({
          title: 'Error',
          message: errorMessage
        })
      } finally {
        this.isButtonLoading = false
      }
    },

    generateCaptcha() {
      if (Math.random() < 0.5) {
        this.num1 = Math.floor(Math.random() * 9) + 1
        this.num2 = Math.floor(Math.random() * 90) + 10
      } else {
        this.num1 = Math.floor(Math.random() * 90) + 10
        this.num2 = Math.floor(Math.random() * 9) + 1
      }
      this.captchaAnswer = null
    },

    async sendRecoveryLink() {
      if (!this.selectedMobileNumber) return

      const expected = this.num1 + this.num2
      if (this.captchaAnswer !== expected) {
        iziToast.error({
          title: 'Error',
          message: 'Incorrect answer. Please try again.'
        })
        this.generateCaptcha() // reset with new random numbers
        return
      }
      try {
        this.isButtonLoading = true
        const response = await sendNewPassword(
          this.username,
          this.selectedMobileNumber
        )

        // console.log(response)
        this.isButtonLoading = false

        if (response.status === false) {
          iziToast.error({
            title: 'Error',
            message:
              response.message || 'Failed to send SMS. Please try again later.'
          })
        } else {
          iziToast.success({
            title: 'Recovery Link Sent',
            message:
              'A new password has been sent to your selected mobile number. Please login with your new password.'
          })
          this.step = 3
        }
      } catch (e) {
        iziToast.error({
          title: 'Error',
          message: 'An Unknown error occurred. Please try again.'
        })
      } finally {
        this.isButtonLoading = false
      }
    },
    goToLoginHandler() {
      if (this.$route.path === '/') {
        // On homepage, emit event for modal
        this.$emit('goForLogin')
      } else {
        // On password recovery page, redirect to login page
        this.$router.push('/login')
      }
    }
  },
  mounted() { 
    this.generateCaptcha(),
    this.$nextTick(() => {
      this.loading = false
    })
  }
}
</script>

<style scoped>
/* Modern Login Wrapper */
.modern-password-reset-wrapper {
  display: flex;
  align-items: center;
  background: #f5f7fa;
  padding: 20px 0;
}

.password-reset-page {
  width: 100%;
}

.password-reset-wrapper {
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* password-reset Card */
.password-reset-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 520px;
  width: 100%;
  margin: auto;
  overflow: hidden;
}

/* password-reset Header */
.password-reset-header {
  text-align: center;
  padding: 50px 30px;
  background: #4e73a8;
}

.logo-img {
  max-width: 90px;
  height: auto;
}

/* password-reset Form */
.password-reset-form {
  padding: 32px 40px 36px;
}

/* Form Group */
.form-group-modern {
  margin-bottom: 18px;
}

.input-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 6px;
  letter-spacing: 0.2px;
}

/* Input Wrapper */
.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 15px;
  z-index: 1;
}

.modern-input {
  width: 100%;
  padding: 11px 14px 11px 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #fafbfc;
}

.modern-input:focus {
  outline: none;
  border-color: #2c5aa0;
  background: white;
  box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.08);
}

.modern-input::placeholder {
  color: #a0aec0;
  font-size: 13px;
}

/* Remember Me */
.remember-row {
  margin-bottom: 18px;
  display: flex;
  align-items: center;
}

.modern-checkbox {
  font-size: 13px;
  color: #4a5568;
}

/* password-reset Button */
.password-reset-button {
  width: 100%;
  padding: 11px 20px;
  background: #2c5aa0;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(44, 90, 160, 0.25);
}

.password-reset-button:hover {
  background: #1e4080;
  box-shadow: 0 4px 12px rgba(44, 90, 160, 0.35);
}

.password-reset-button:active {
  transform: translateY(1px);
}

.password-reset-button.loading {
  background: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

.password-reset-button.loading:hover {
  background: #94a3b8;
}

.password-reset-button i {
  margin-right: 6px;
  font-size: 13px;
}

/* Responsive Design */
@media (max-width: 576px) {
  .password-reset-card {
    max-width: 100%;
    border-radius: 12px;
    margin: 0 15px;
  }

  .password-reset-header {
    padding: 35px 20px;
  }

  .logo-img {
    max-width: 70px;
  }

  .password-reset-form {
    padding: 26px 28px 30px;
  }

  .form-group-modern {
    margin-bottom: 16px;
  }

  .modern-input {
    padding: 10px 12px 10px 38px;
    font-size: 13px;
  }

  .input-icon {
    left: 12px;
    font-size: 14px;
  }

  .password-reset-button {
    padding: 10px 18px;
    font-size: 13px;
  }
}

/* Smooth animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.password-reset-card {
  animation: fadeIn 0.4s ease;
}
</style>
